/**
 * BuildingRenderer.ts
 * Renders player-placed buildings as isometric blocks.
 * Also draws the placement-mode ghost preview.
 */
import { Container, Graphics, Text, TextStyle } from 'pixi.js';
import type { PlacedBuilding } from '../types';
import type { TileMap } from '../map/TileMap';
import { BUILDING_DEFS } from '../systems/PlacementSystem';
import { ISO_TILE_WIDTH, ISO_TILE_HEIGHT, ELEVATION_STEP } from '../constants';

const HW   = ISO_TILE_WIDTH  / 2;
const HH   = ISO_TILE_HEIGHT / 2;
const STEP = ELEVATION_STEP;

const BSCALE = 0.76;
const BW = HW * BSCALE;
const BH = HH * BSCALE;

const TOPPER_BUILDING     = 1.2;
const TOPPER_CONSTRUCTING = 0.5;
const OUTLINE = 0x070a0e;

const NAME_STYLE = new TextStyle({
  fontFamily: 'Menlo, Consolas, monospace',
  fontSize: 10, fill: '#ccddff', align: 'center',
});
const GHOST_VALID_COLOUR   = 0x44ffaa;
const GHOST_INVALID_COLOUR = 0xff4444;

function shade(color: number, factor: number): number {
  const r = Math.min(255, Math.round(((color >> 16) & 0xff) * factor));
  const g = Math.min(255, Math.round(((color >> 8)  & 0xff) * factor));
  const b = Math.min(255, Math.round((color & 0xff) * factor));
  return (r << 16) | (g << 8) | b;
}

function drawIsoBlock(
  g: Graphics, map: TileMap,
  col: number, row: number,
  extraLevels: number, topColour: number, alpha = 1.0,
): void {
  const base = map.tileBase(col, row);
  const elev = map.elevation(col, row);
  const EY   = base.y - elev * STEP;
  const h    = extraLevels * STEP;

  g.lineStyle(0);
  g.beginFill(shade(topColour, 0.60), alpha);
  g.drawPolygon([
    base.x + BW, EY - h,
    base.x,      EY - h + BH,
    base.x,      EY + BH,
    base.x + BW, EY,
  ]);
  g.endFill();

  g.beginFill(shade(topColour, 0.38), alpha);
  g.drawPolygon([
    base.x - BW, EY - h,
    base.x,      EY - h + BH,
    base.x,      EY + BH,
    base.x - BW, EY,
  ]);
  g.endFill();

  g.lineStyle(1, OUTLINE, 0.35);
  g.beginFill(topColour, alpha);
  g.drawPolygon([
    base.x,      EY - h - BH,
    base.x + BW, EY - h,
    base.x,      EY - h + BH,
    base.x - BW, EY - h,
  ]);
  g.endFill();
  g.lineStyle(0);
}

export class BuildingRenderer {
  private readonly g      = new Graphics();
  private readonly ghostG = new Graphics();
  private readonly labels: Text[] = [];

  constructor(
    private readonly world: Container,
    private readonly map:   TileMap,
  ) {
    world.addChild(this.g);
    world.addChild(this.ghostG);
  }

  render(buildings: PlacedBuilding[], _elapsed: number): void {
    const g = this.g;
    g.clear();

    // Ensure enough label Text objects
    while (this.labels.length < buildings.length) {
      const t = new Text('', NAME_STYLE);
      t.anchor.set(0.5, 1);
      this.world.addChild(t);
      this.labels.push(t);
    }

    // Sort back-to-front (painter's order)
    const order = buildings
      .map((b, i) => ({ b, i }))
      .sort((a, z) => (a.b.col + a.b.row) - (z.b.col + z.b.row));

    for (const { b, i } of order) {
      const def   = BUILDING_DEFS.find(d => d.type === b.type)!;
      const label = this.labels[i];

      if (!b.constructed) {
        const h = TOPPER_CONSTRUCTING + b.constructionProgress * (TOPPER_BUILDING - TOPPER_CONSTRUCTING);
        drawIsoBlock(g, this.map, b.col, b.row, h, def.colour, 0.55);

        // Construction progress bar
        const base  = this.map.tileBase(b.col, b.row);
        const elev  = this.map.elevation(b.col, b.row);
        const topY  = base.y - elev * STEP - h * STEP - BH;
        const barW  = HW * 1.6;
        const barX  = base.x - barW / 2;
        g.lineStyle(0);
        g.beginFill(0x1a1a1a, 0.75);
        g.drawRect(barX, topY - 8, barW, 3);
        g.endFill();
        g.beginFill(def.colour, 0.9);
        g.drawRect(barX, topY - 8, barW * b.constructionProgress, 3);
        g.endFill();

        label.text = def.name;
        label.position.set(base.x, topY - 12);
      } else {
        drawIsoBlock(g, this.map, b.col, b.row, TOPPER_BUILDING, def.colour);

        // HP bar (only when damaged)
        if (b.hp < b.maxHp) {
          const base = this.map.tileBase(b.col, b.row);
          const elev = this.map.elevation(b.col, b.row);
          const topY = base.y - elev * STEP - TOPPER_BUILDING * STEP - BH;
          const pct  = b.hp / b.maxHp;
          const barW = HW * 1.6;
          const barX = base.x - barW / 2;
          g.lineStyle(0);
          g.beginFill(0x330000, 0.8);
          g.drawRect(barX, topY - 6, barW, 3);
          g.endFill();
          g.beginFill(pct > 0.5 ? 0x44cc44 : 0xff8800, 0.9);
          g.drawRect(barX, topY - 6, barW * pct, 3);
          g.endFill();
        }

        label.text = '';  // name hidden once constructed to reduce clutter
      }
    }

    // Hide unused labels
    for (let i = order.length; i < this.labels.length; i++) {
      this.labels[i].text = '';
    }
  }

  renderGhost(
    col: number | null, row: number | null,
    valid: boolean,
  ): void {
    this.ghostG.clear();
    if (col === null || row === null) return;
    const colour = valid ? GHOST_VALID_COLOUR : GHOST_INVALID_COLOUR;
    drawIsoBlock(this.ghostG, this.map, col, row, TOPPER_BUILDING, colour, 0.45);
  }
}
